// app/shop/tinctures/page.tsx
"use client";

import { getProductsByCategory } from "@/lib/api/dutchie";
import CategoryLayout         from "@/components/shop/CategoryLayout";
import type { ProductDisplay } from "@/lib/api/dutchie";
import {
  extractPotencyValue,   // 👉 reuse from your current file or utils
  normalizeWeight,
} from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Config tables                                                     */
/* ------------------------------------------------------------------ */

const EFFECTS = [
  "Relaxed","Calm","Sleepy","Happy","Energetic",
  "Focused","Balanced","Wellness","Relief","Clear Mind","Recovery",
];

const SUBCATEGORY_REGEX: [RegExp,string][] = [
  [/full.?spectrum/i, "Full Spectrum"],
  [/cbd.?dominant/i,  "CBD Dominant"],
  [/thc.?dominant/i,  "THC Dominant"],
];

/* ------------------------------------------------------------------ */
/*  Derivations                                                       */
/* ------------------------------------------------------------------ */

/** Map raw product ➜ virtual sub‑category label */
const categorizeTincture = (p: ProductDisplay) => {
  const txt = `${p.name ?? ""} ${p.description ?? ""}`.toLowerCase();
  for (const [re, label] of SUBCATEGORY_REGEX) if (re.test(txt)) return label;
  return "Regular";
};

/** Lightweight enhancer (dedupe + virtual props) */
const enhance = (p: ProductDisplay): ProductDisplay => {
  const thc = extractPotencyValue(p, "thc");   // returns % 0‑100
  const cbd = extractPotencyValue(p, "cbd");

  return {
    ...p,
    virtualSubcategory: categorizeTincture(p),
    normalizedWeight  : normalizeWeight(p.option ?? ""),
    thc: thc.toString(),   // keep numeric strings so CategoryLayout can filter
    cbd: cbd.toString(),
  };
};

/* ------------------------------------------------------------------ */
/*  Fetch                                                             */
/* ------------------------------------------------------------------ */

const fetchProducts = async (): Promise<ProductDisplay[]> => {
  // grab only the tincture rows that are actually on‑hand
  const raw = (await getProductsByCategory("tinctures")).filter(p => p.inStock);

  // dedupe
  const unique = Array.from(new Map(raw.map(p => [p.id, p])).values());

  return unique.map(enhance);
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export default function TincturesPage() {
  const config = {
    title          : "Tinctures",
    blurb          : "Precision‑dosed cannabis tinctures for sublingual use",
    fetch          : fetchProducts,
    headerTheme    : "teal" as const,
    effects        : EFFECTS,
    potencyUnit    : "%",
    showPotency    : true,
    filterInStock  : false,
  };

  return (
    <CategoryLayout
      config={config}
      productCardProps={{ buttonClassName: "bg-teal-600 hover:bg-teal-700" }}
    />
  );
}
